// src/pages/Documents.jsx (Admin) — updated notify template + single-notify-only for bulk
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Button, Chip, LinearProgress, Collapse, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Checkbox, Snackbar, Alert, Divider, List, ListItem, ListItemText,
  ListItemIcon, Grid
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import {
  collection, getDocs, onSnapshot, query, doc, setDoc, getDoc
} from "firebase/firestore";
import { onValue, ref, get } from "firebase/database";
import { firestore, db as rtdb, functions as fbFunctions } from "../../firebase";
import { httpsCallable } from "firebase/functions";

/* ---------------- constants (doc keys) ---------------- */
const BASE_DOCS = [
  { key: "cv", title: "CV / Resume", required: true },
  { key: "pan", title: "PAN Card", required: true },
  { key: "aadhaar", title: "Aadhaar Card", required: true },
  { key: "tenth", title: "10th Marksheet + Certificate", required: true },
  { key: "highest", title: "Highest Education Marksheet + Certificate", required: true },
];
const EMP_DOCS = [
  { key: "salarySlip", title: "Last Organization Salary Slip", required: true },
  { key: "experienceLetter", title: "Experience Letter", required: true },
  { key: "noc", title: "NOC Letter", required: true },
];
const ts = () => new Date().toISOString();

/* ---------------- helpers ---------------- */
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

function completionForRow(row) {
  const requiredTotal = BASE_DOCS.length + (row.experienced ? EMP_DOCS.length : 0);
  const requiredUploaded =
    BASE_DOCS.filter(d => row.filesMap[d.key]?.url).length +
    (row.experienced ? EMP_DOCS.filter(d => row.filesMap[d.key]?.url).length : 0);
  return { requiredUploaded, requiredTotal, complete: requiredUploaded === requiredTotal };
}

function pendingDocsForRow(row) {
  const pend = [];
  BASE_DOCS.forEach(d => { if (d.required && !row.filesMap[d.key]?.url) pend.push({ key: d.key, title: d.title }); });
  if (row.experienced) {
    EMP_DOCS.forEach(d => { if (d.required && !row.filesMap[d.key]?.url) pend.push({ key: d.key, title: d.title }); });
  }
  return pend;
}

/* Row component */
function EmployeeRow({
  index,
  row,
  onToggleResubmitAsk,
  onNotify,
  onOpenPdf,
  onAskDownload,
}) {
  const [open, setOpen] = useState(false);
  const { requiredUploaded, requiredTotal, complete } = completionForRow(row);

  const listMain = BASE_DOCS.map(d => ({ ...d, meta: row.filesMap[d.key] || null }));
  const listEmp  = EMP_DOCS.map(d => ({ ...d, required: row.experienced, meta: row.filesMap[d.key] || null }));

  return (
    <>
      <TableRow hover>
        <TableCell width={56} align="center" sx={{ fontWeight: 600 }}>
          {index + 1}
        </TableCell>

        <TableCell padding="checkbox" width={48}>
          <IconButton size="small" onClick={() => setOpen(v => !v)}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Stack spacing={0.25}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontWeight: 700 }}>{row.name || "Unknown"}</Typography>
              <Chip
                size="small"
                variant={row.experienced ? "filled" : "outlined"}
                color={row.experienced ? "primary" : "default"}
                label={row.experienced ? "Experienced" : "Fresher"}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">UID: {row.uid}</Typography>
          </Stack>
        </TableCell>

        <TableCell>
          <Stack spacing={0.5} sx={{ minWidth: 220 }}>
            <Typography variant="body2">Required: {requiredUploaded}/{requiredTotal}</Typography>
            <LinearProgress variant="determinate" value={pct(requiredUploaded, requiredTotal)} />
          </Stack>
        </TableCell>

        <TableCell align="right" width={220}>
          <Tooltip title={complete ? "All required docs submitted" : "Notify employee to upload remaining docs"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<NotificationsActiveIcon />}
                disabled={complete}
                onClick={() => onNotify(row.uid)}
              >
                Notify
              </Button>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ bgcolor: "background.paper", p: 2, borderTop: "1px dashed", borderColor: "divider" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Required Documents
              </Typography>
              <DocMatrix
                items={listMain}
                uid={row.uid}
                onToggleResubmitAsk={onToggleResubmitAsk}
                onOpenPdf={onOpenPdf}
                onAskDownload={onAskDownload}
              />

              <Divider sx={{ my: 2 }} />

              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Employment Documents
                </Typography>
                {!row.experienced && <Chip size="small" variant="outlined" label="Optional for Fresher" />}
              </Stack>
              <DocMatrix
                items={listEmp}
                uid={row.uid}
                onToggleResubmitAsk={onToggleResubmitAsk}
                onOpenPdf={onOpenPdf}
                onAskDownload={onAskDownload}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

/* DocMatrix unchanged */
function DocMatrix({ items, uid, onToggleResubmitAsk, onOpenPdf, onAskDownload }) {
  return (
    <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
      {items.map((it, idx) => {
        const uploaded = !!it.meta?.url;
        const unlocked = !!it.meta?.allowResubmit;
        return (
          <Stack
            key={it.key}
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{
              p: 1,
              borderRadius: 1,
              ...(idx !== items.length - 1 ? { borderBottom: "1px dashed", borderColor: "divider" } : {}),
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <PictureAsPdfIcon color={uploaded ? "primary" : "disabled"} />
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{it.title}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25, flexWrap: "wrap" }}>
                  {uploaded ? (
                    <Chip size="small" color="success" label="Uploaded" />
                  ) : it.required ? (
                    <Chip size="small" color="warning" label="Required" />
                  ) : (
                    <Chip size="small" variant="outlined" label="Optional" />
                  )}
                  {uploaded && (
                    <Chip size="small" variant="outlined" label={it.meta?.status || "SUBMITTED"} />
                  )}
                  {uploaded && (
                    unlocked ? (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LockOpenIcon fontSize="small" color="primary" />
                        <Typography variant="caption">Re-submit enabled</Typography>
                      </Stack>
                    ) : (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LockIcon fontSize="small" color="action" />
                        <Typography variant="caption">Locked</Typography>
                      </Stack>
                    )
                  )}
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1}>
              {uploaded && (
                <Button
                  size="small"
                  variant="text"
                  startIcon={<VisibilityIcon />}
                  onClick={() => onOpenPdf(it.meta.url, `${uid} — ${it.title}`)}
                >
                  View
                </Button>
              )}
              {uploaded && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => onAskDownload(it.meta.url, `${uid} — ${it.title}.pdf`)}
                >
                  Download
                </Button>
              )}

              <Tooltip title="Allow employee to re-upload this document">
                <span>
                  <Checkbox
                    size="small"
                    checked={!!it.meta?.allowResubmit}
                    onChange={() => onToggleResubmitAsk(uid, it.key, !!it.meta?.allowResubmit)}
                    disabled={!uploaded && !it.meta}
                  />
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        );
      })}
    </Paper>
  );
}

/* MAIN */
export default function AdminDocuments() {
  const [empList, setEmpList] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewer, setViewer] = useState({ open: false, url: "", title: "" });
  const [confirmDownload, setConfirmDownload] = useState({ open: false, url: "", title: "" });
  const [confirmToggle, setConfirmToggle] = useState({ open: false, uid: "", docKey: "", curr: false });

  const [snack, setSnack] = useState({ open: false, sev: "success", msg: "" });

  // notify dialog state (single or bulk)
  const [notifyDialog, setNotifyDialog] = useState({
    open: false,
    type: "single",
    uid: null,
    name: "",
    pending: [],
    targets: [],
  });

  /* load RTDB employees */
  useEffect(() => {
    const unsub = onValue(ref(rtdb, "employees"), async (snap) => {
      const data = snap.val() || {};
      const arr = Object.entries(data)
        .filter(([, v]) => !(v?.onboardingStatus === "OFFBOARDED" || v?.active === false))
        .map(([uid, v]) => ({ uid, name: v?.fullName || "Unknown" }));

      setEmpList(arr);
    });
    return () => unsub();
  }, []);

  /* load docs for each employee from Firestore */
  const loadDocsFor = async (uid) => {
    let experienced = false;
    try {
      const p = await getDoc(doc(firestore, "employeeDocs", uid));
      experienced = !!p.data()?.profile?.experienced;
    } catch {}
    const filesMap = {};
    try {
      const fs = await getDocs(collection(firestore, "employeeDocs", uid, "files"));
      fs.forEach((f) => (filesMap[f.id] = { id: f.id, ...f.data() }));
    } catch {}
    return { experienced, filesMap };
  };

  const hydrateAll = async (list) => {
    setLoading(true);
    try {
      const enriched = await Promise.all(
        list.map(async (e) => {
          const add = await loadDocsFor(e.uid);
          return { ...e, ...add };
        })
      );
      setRows(enriched);
    } catch (e) {
      setSnack({ open: true, sev: "error", msg: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!empList.length) {
      setRows([]);
      setLoading(false);
      return;
    }
    hydrateAll(empList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empList]);

  const onRefresh = () => hydrateAll(empList);

  const onOpenPdf = (url, title) => setViewer({ open: true, url, title });
  const onAskDownload = (url, title) => setConfirmDownload({ open: true, url, title });
  const doDownload = () => {
    const a = document.createElement("a");
    a.href = confirmDownload.url;
    a.download = confirmDownload.title || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setConfirmDownload({ open: false, url: "", title: "" });
  };

  const onToggleResubmitAsk = (uid, docKey, curr) => {
    setConfirmToggle({ open: true, uid, docKey, curr });
  };

  const doToggleResubmit = async () => {
    const { uid, docKey, curr } = confirmToggle;
    const next = !curr;
    setRows((prev) =>
      prev.map((r) =>
        r.uid !== uid
          ? r
          : {
              ...r,
              filesMap: {
                ...r.filesMap,
                [docKey]: { ...(r.filesMap[docKey] || {}), allowResubmit: next, updatedAt: ts() },
              },
            }
      )
    );

    try {
      await setDoc(
        doc(firestore, "employeeDocs", uid, "files", docKey),
        { allowResubmit: next, updatedAt: ts() },
        { merge: true }
      );
      setSnack({ open: true, sev: "success", msg: next ? "Re-submit enabled." : "Re-submit locked." });
    } catch (e) {
      setRows((prev) =>
        prev.map((r) =>
          r.uid !== uid
            ? r
            : {
                ...r,
                filesMap: {
                  ...r.filesMap,
                  [docKey]: { ...(r.filesMap[docKey] || {}), allowResubmit: curr, updatedAt: ts() },
                },
              }
        )
      );
      setSnack({ open: true, sev: "error", msg: e.message });
    } finally {
      setConfirmToggle({ open: false, uid: "", docKey: "", curr: false });
    }
  };

  /* Individual notify: open dialog showing pending docs for uid */
  const onNotify = async (uid) => {
    const row = rows.find(r => r.uid === uid);
    if (!row) {
      setSnack({ open: true, sev: "error", msg: "Employee not found." });
      return;
    }
    const pending = pendingDocsForRow(row);
    if (!pending.length) {
      setSnack({ open: true, sev: "info", msg: `${row.name} has no pending documents.` });
      return;
    }

    setNotifyDialog({
      open: true,
      type: "single",
      uid,
      name: row.name,
      pending,
      targets: []
    });
  };

  /* Bulk: prepare list of targets with pending counts + titles, open dialog (single dialog only) */
  const openNotifyAll = () => {
    const targets = rows
      .map(r => {
        const pend = pendingDocsForRow(r);
        return { uid: r.uid, name: r.name, pendingCount: pend.length, pendingTitles: pend.map(p => p.title) };
      })
      .filter(t => t.pendingCount > 0);

    // open the single notifyDialog (bulk mode) only — avoids the old double-popup issue
    setNotifyDialog({
      open: true,
      type: "bulk",
      uid: null,
      name: "",
      pending: [],
      targets
    });
  };

  /* Send emails (single or bulk) using callable sendMailCallable */
  const doSendNotifications = async () => {
    const fn = httpsCallable(fbFunctions, "sendMailCallable");
    const dialog = notifyDialog;
    if (!dialog.open) return;

    let recipients = [];
    if (dialog.type === "single") {
      recipients = [{ uid: dialog.uid, name: dialog.name, pendingTitles: dialog.pending.map(p => p.title) }];
    } else {
      recipients = dialog.targets || [];
    }
    if (!recipients.length) {
      setSnack({ open: true, sev: "info", msg: "No recipients to notify." });
      setNotifyDialog(d => ({ ...d, open: false }));
      return;
    }

    // Confirm total count in title already; proceed to send
    setSnack({ open: true, sev: "info", msg: `Sending notifications to ${recipients.length} employee(s)...` });

    const calls = recipients.map(async (r) => {
      try {
        const snap = await get(ref(rtdb, `employees/${r.uid}/email`));
        const to = snap.val();
        if (!to) throw new Error("Email missing");

        // Choose subject
        const subject =
          dialog.type === "single"
            ? "Regarding document upload"
            : "Action required: Complete your profile by uploading documents";

        // Build the professional email copy provided (dynamically include pending list)
        const listHtml = (r.pendingTitles && r.pendingTitles.length)
          ? `<ul>${r.pendingTitles.map(x => `<li>${x}</li>`).join("")}</ul>`
          : `<p>The required documents are pending in your employee portal.</p>`;

        const portalSteps = `
          <p><strong>Portal link:</strong> <a href="https://thinkersclub.online/login">https://thinkersclub.online/login</a></p>
          <p><strong>Follow the steps:</strong><br/>
            Login → Home → Click top-right corner (three lines) → In HR section select Documents → Upload and submit.
            If you are a fresher, toggle "Fresher" and submit the applicable documents.
          </p>
        `;

        const html = `<!doctype html><html><body style="font-family: system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif; color:#0f172a;">
          <p>Dear ${r.name || ""},</p>
          <p>We hope you're settling in well!</p>
          <p>As part of completing your onboarding/documentation formalities, the following documents are still pending in your employee portal:</p>
          ${listHtml}
          ${portalSteps}
          <p>If you’ve already uploaded these documents and are seeing this email in error, please ignore it. In case you face difficulty while uploading, feel free to reply to this email or contact HR.</p>
          <p>Thank you for your prompt attention to this.</p>
          <p>HR — Thinkers Club</p>
        </body></html>`;

        const resp = await fn({ to, subject, html });
        return { uid: r.uid, ok: true, info: resp?.data || resp };
      } catch (err) {
        return { uid: r.uid, ok: false, error: String(err?.message || err) };
      }
    });

    const results = await Promise.all(calls);
    const succeeded = results.filter(r => r.ok).length;
    const failed = results.length - succeeded;

    setSnack({ open: true, sev: failed ? "warning" : "success", msg: `Notifications completed. Success: ${succeeded}, Failed: ${failed}` });

    setNotifyDialog(d => ({ ...d, open: false }));
  };

  const closeNotifyDialog = () => {
    setNotifyDialog(d => ({ ...d, open: false, targets: [] }));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Employee Documents</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            startIcon={<NotificationsActiveIcon />}
            onClick={openNotifyAll}
          >
            Notify Incomplete
          </Button>
          <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh}>
            Refresh
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2 }}>
        {loading && <LinearProgress />}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={56} align="center">SR</TableCell>
              <TableCell width={48} />
              <TableCell>Employee</TableCell>
              <TableCell>Completion</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                  No employees found.
                </TableCell>
              </TableRow>
            )}

            {rows.map((r, idx) => (
              <EmployeeRow
                key={r.uid}
                index={idx}
                row={r}
                onToggleResubmitAsk={onToggleResubmitAsk}
                onNotify={onNotify}
                onOpenPdf={onOpenPdf}
                onAskDownload={onAskDownload}
              />
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* PDF Viewer */}
      <Dialog open={viewer.open} onClose={() => setViewer({ open: false, url: "", title: "" })} fullWidth maxWidth="lg">
        <DialogTitle>{viewer.title || "Document"}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {viewer.url ? (
            <iframe title="pdf" src={viewer.url} style={{ width: "100%", height: "75vh", border: 0 }} />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">No preview available.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewer({ open: false, url: "", title: "" })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Download confirm */}
      <Dialog open={confirmDownload.open} onClose={() => setConfirmDownload({ open: false, url: "", title: "" })}>
        <DialogTitle>Download document?</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to download <b>{confirmDownload.title || "this PDF"}</b>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDownload({ open: false, url: "", title: "" })}>Cancel</Button>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={doDownload}>
            Yes, download
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toggle allowResubmit confirm */}
      <Dialog
        open={confirmToggle.open}
        onClose={() => setConfirmToggle({ open: false, uid: "", docKey: "", curr: false })}
      >
        <DialogTitle>{confirmToggle.curr ? "Lock re-submit?" : "Allow re-submit?"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {confirmToggle.curr
              ? "The employee will no longer be able to upload a replacement for this document."
              : "The employee will be able to upload a replacement for this document."}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            UID: {confirmToggle.uid} • Doc: {confirmToggle.docKey}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmToggle({ open: false, uid: "", docKey: "", curr: false })}>
            Cancel
          </Button>
          <Button variant="contained" onClick={doToggleResubmit}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* NOTIFY DIALOG (single or bulk) */}
      <Dialog open={notifyDialog.open} onClose={closeNotifyDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {notifyDialog.type === "single"
            ? `Notify ${notifyDialog.name}`
            : `Notify ${notifyDialog.targets?.length || 0} employee(s) — pending documents`}
        </DialogTitle>
        <DialogContent dividers>
          {notifyDialog.type === "single" ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                The following required documents are pending for <b>{notifyDialog.name}</b>. An email will be sent asking them to upload these.
              </Typography>
              <List dense>
                {notifyDialog.pending.map(p => (
                  <ListItem key={p.key}>
                    <ListItemIcon><PictureAsPdfIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={p.title} />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                These employees have pending required documents. Only employees with pending items will be notified.
              </Typography>
              <List dense sx={{ maxHeight: 360, overflow: "auto" }}>
                {notifyDialog.targets.map(t => (
                  <ListItem key={t.uid} alignItems="flex-start">
                    <Grid container>
                      <Grid item xs={8}>
                        <ListItemText primary={t.name} secondary={<span style={{ opacity: 0.8 }}>{t.uid}</span>} />
                      </Grid>
                      <Grid item xs={4} style={{ textAlign: "right" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{t.pendingCount} pending</Typography>
                        <Typography variant="caption" color="text.secondary">{t.pendingTitles.slice(0,3).join(", ")}{t.pendingTitles.length>3?"…":""}</Typography>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeNotifyDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<NotificationsActiveIcon />}
            onClick={doSendNotifications}
            disabled={notifyDialog.type === "bulk" ? (notifyDialog.targets?.length === 0) : (notifyDialog.pending?.length === 0)}
          >
            Notify now
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={2600} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
